import { NgModule } from '@angular/core';
import type { Route } from '@angular/router';
import { RouterModule } from '@angular/router';
import type { NotFoundRouteConfig } from '@freelancer/404';
import { NotFoundGuard } from '@freelancer/404';
import { AuthGuard } from '@freelancer/auth-guard';
import type { PreloadRouteConfig } from '@freelancer/preload-route';
import { RedirectToPhpGuard } from '@freelancer/redirect-to-php';
import { ContainerSize } from '@freelancer/ui/container';
import { AppEmptyComponent } from './app-empty.component';
import { AppEmptyModule } from './app-empty.module';
import type { LoggedInShellConfig, LoggedOutShellConfig } from './app-shell';
import { FreightlancerPageGuard } from './freightlancer/freightlancer-page-guard.service';
import { RedirectToNewCarrierGuard } from './freightlancer/redirect-to-new-carrier-guard.service';
import { jobsRouteMatcher } from './jobs/job-route-matcher';
import { masterArticleRouteMatcher } from './master-article/master-article-route-matcher';
import { RedirectToDashboardGuard } from './redirect-to-dashboard-guard.service';
import { RedirectToRootMasterArticleGuard } from './redirect-to-root-master-article-guard.service';
import { RedirectToThemeGuard } from './redirect-to-theme-guard.service';
import { LegacyUserSettingsGuard } from './user-settings/legacy-user-settings-guard.service';

// WARNING: Non lazy-loaded components are strictly forbidden here: you MUST use
// loadChildren if you want to add a new top-level route.
// eslint-disable-next-line local-rules/readonly-array
export const routes: (Route & {
  data?:
    | LoggedInShellConfig
    | PreloadRouteConfig
    | NotFoundRouteConfig
    | LoggedOutShellConfig;
})[] = [
  {
    path: '',
    canActivate: [RedirectToDashboardGuard],
    data: {
      containerSizeDesktopXLarge: ContainerSize.DESKTOP_XLARGE,
      enableSeoNav: true,
      isDarkMode: true,
    },
    pathMatch: 'full',
    children: [
      {
        path: '',
        title: $localize`Home`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        loadChildren: () =>
          import('app/home/home-page.module').then(m => m.HomePageModule),
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'login',
    title: $localize`Login`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableGiveGetFooter: true,
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    loadChildren: () =>
      import('app/login-signup/login-page/login-page.module').then(
        m => m.LoginPageModule,
      ),
  },
  {
    path: 'signup',
    title: $localize`Sign Up`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableGiveGetFooter: true,
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    loadChildren: () =>
      import('app/login-signup/signup-page/signup-page.module').then(
        m => m.SignupPageModule,
      ),
  },
  {
    path: 'new-account',
    title: $localize`Create Account`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableGiveGetFooter: true,
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    loadChildren: () =>
      import('app/login-signup/new-account-page/new-account-page.module').then(
        m => m.NewAccountPageModule,
      ),
  },
  // the `login` route defined above will override the one inside `@freelancer/login`.
  {
    path: '',
    title: $localize`Login`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('@freelancer/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'reset-user-password',
    title: $localize`Reset Password`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    loadChildren: () =>
      import(
        'app/login-signup/reset-user-password/reset-user-password.module'
      ).then(m => m.ResetUserPasswordModule),
  },
  {
    path: 'users/reset_user_password.php',
    redirectTo: 'reset-user-password',
  },
  {
    path: 'messages',
    title: $localize`Inbox`,
    data: {
      containerSize: ContainerSize.FLUID,
      disableFooter: true,
      disableMessaging: true,
      fixedHeight: true,
      hideIosAccessoryBar: true,
      preloadRequired: true,
      useManualIosKeyboardAvoidance: true,
    },
    loadChildren: () =>
      import('app/messaging/messaging-inbox/messaging-inbox.module').then(
        m => m.MessagingInboxModule,
      ),
  },
  {
    path: 'calls',
    title: $localize`Call Log`,
    canActivate: [FreightlancerPageGuard],
    data: {
      containerSize: ContainerSize.FLUID,
      disableFooter: true,
      disableMessaging: true,
      fixedHeight: true,
    },
    loadChildren: () =>
      import('app/call-log/call-log-page.module').then(
        m => m.CallLogPageModule,
      ),
  },
  {
    path: 'thread-redirect',
    title: $localize`Thread redirect`,
    data: {
      containerSize: ContainerSize.FLUID,
      disableChildNavigationActions: true,
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      fixedHeight: true,
    },
    loadChildren: () =>
      import('app/thread-redirect-page/thread-redirect-page.module').then(
        m => m.ThreadRedirectPageModule,
      ),
  },
  {
    path: 'dashboard',
    title: $localize`Dashboard`,
    data: {
      hideIosAccessoryBar: true,
      isMobilePrimaryView: true,
      enableSidebar: true,
      enableMobileVerificationBanner: true,
      stickyPrimaryNav: true,
      showAndHideNavOnScroll: true,
      preloadRequired: true,
    },
    loadChildren: () =>
      import('app/dashboard/dashboard.module').then(m => m.DashboardModule),
  },
  {
    path: 'dashboard/',
    redirectTo: 'dashboard',
    pathMatch: 'full',
    data: {
      stickyPrimaryNav: true,
      showAndHideNavOnScroll: true,
    },
  },
  {
    path: 'credit-terms/butn',
    title: $localize`Credit Terms`,
    canActivate: [FreightlancerPageGuard],
    loadChildren: () =>
      import(
        'app/freightlancer/freightlancer-butn-landing-page/freightlancer-butn-landing-page.module'
      ).then(m => m.FreightlancerButnLandingPageModule),
  },
  {
    path: 'milestone-dispute-policy',
    title: $localize`Milestone Dispute Policy`,
    canActivate: [FreightlancerPageGuard],
    loadChildren: () =>
      import(
        'app/freightlancer/freightlancer-milestone-dispute-policy-page/freightlancer-milestone-dispute-policy-page.module'
      ).then(m => m.FreightlancerMilestoneDisputePolicyPageModule),
  },
  {
    path: 'auction-transport/grays-online',
    title: $localize`Grays Online Transport`,
    canActivate: [FreightlancerPageGuard],
    loadChildren: () =>
      import(
        'app/freightlancer/freightlancer-grays-landing-page/freightlancer-grays-landing-page.module'
      ).then(m => m.FreightlancerGraysLandingPageModule),
  },
  {
    path: 'widget/price-estimator',
    title: $localize`Price Estimator`,
    canActivate: [FreightlancerPageGuard],
    data: {
      disableMessaging: true,
      disableFooter: true,
      disableNavigation: true,
      disableChildNavigationActions: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('app/freightlancer/price-estimator/price-estimator.module').then(
        m => m.FreightlancerPriceEstimatorModule,
      ),
  },
  {
    path: 'projects',
    title: $localize`Project Details`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      enableSidebar: true,
      isMobilePrimaryView: true,
      showBackButton: true,
      redirectToThemeFilter: ['freightlancer'],
      preloadModule: 'app/projects/project-view/project-view.module',
    },
    loadChildren: () =>
      import('app/projects/project-view/project-view.module').then(
        m => m.ProjectViewModule,
      ),
  },
  {
    path: 'contest',
    title: $localize`Contest Details`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSidebar: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/contests/contests.module').then(m => m.ContestsModule),
  },
  {
    path: 'post-project',
    title: $localize`Post a Project`,
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      preloadModule: 'app/jobs/job-post/job-post.module',
      hideIosAccessoryBar: true,
      useManualIosKeyboardAvoidance: true,
    },
    loadChildren: () =>
      import('app/jobs/job-post/job-post.module').then(m => m.JobPostModule),
  },
  {
    path: 'post-load',
    title: $localize`Post a Load`,
    canActivate: [FreightlancerPageGuard],
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      isMobilePrimaryView: true,
      lightStatusBar: true,
    },
    loadChildren: () =>
      import(
        'app/jobs/job-post/freightlancer/freightlancer-job-post.module'
      ).then(m => m.FreightlancerJobPostModule),
  },
  {
    path: 'new-carrier',
    title: $localize`Welcome to Loadshift`,
    canActivate: [FreightlancerPageGuard],
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      lightStatusBar: true,
    },
    loadChildren: () =>
      import(
        'app/freightlancer/carrier-onboarding/carrier-onboarding.module'
      ).then(m => m.CarrierOnboardingModule),
  },
  {
    path: 'complete-your-profile',
    title: $localize`Complete Your Profile`,
    canActivate: [FreightlancerPageGuard],
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      lightStatusBar: true,
    },
    loadChildren: () =>
      import(
        'app/freightlancer/freightlancer-complete-your-profile-page/freightlancer-complete-profile.module'
      ).then(m => m.FreightlancerCompleteProfileModule),
  },
  {
    path: 'freightlancer',
    title: $localize`Freightlancer`,
    loadChildren: () =>
      import('app/freightlancer/freightlancer.module').then(
        m => m.FreightlancerModule,
      ),
  },
  {
    path: 'jobs',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`Jobs`,
        canActivate: [RedirectToPhpGuard],
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'faqs',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`FAQs`,
        canActivate: [NotFoundGuard],
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'branded-communities/post-project',
    title: $localize`Post Project`,
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      isMobilePrimaryView: true,
    },
    loadChildren: () =>
      import(
        'app/jobs/job-post/branded-communities/branded-communities-job-post.module'
      ).then(m => m.BrandedCommunitiesJobPostModule),
  },
  {
    path: 'business-builder',
    title: $localize`Business Builder`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/business-builder/business-builder.module').then(
        m => m.BusinessBuilderModule,
      ),
  },
  {
    path: 'pmi',
    title: $localize`PMI`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableNavigation: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () => import('app/pmi/pmi.module').then(m => m.PmiModule),
  },
  {
    path: 'yara',
    title: $localize`Yara`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableNavigation: true,
      disableVerificationBanner: true,
      disableMessaging: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () => import('app/yara/yara.module').then(m => m.YaraModule),
  },
  {
    path: 'partner-dashboard',
    title: $localize`Partner Dashboard`,
    data: {
      adminOnlyMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      enableSidebar: true,
    },
    loadChildren: () =>
      import('app/partner-dashboard/partner-dashboard.module').then(
        m => m.PartnerDashboardModule,
      ),
  },
  {
    path: 'project-management',
    title: $localize`Technical Co-Pilots`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSizeDesktopXLarge: ContainerSize.DESKTOP_XXLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/co-pilot/co-pilot.module').then(m => m.CoPilotModule),
  },
  {
    path: 'deloitte',
    title: $localize`Deloitte`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableNavigation: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/deloitte/deloitte.module').then(m => m.DeloitteModule),
  },
  {
    path: 'enterprise',
    title: $localize`Enterprise`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSizeDesktopXLarge: ContainerSize.DESKTOP_XXLARGE,
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/enterprise/enterprise.module').then(m => m.EnterpriseModule),
  },
  {
    path: 'globalfleet',
    title: $localize`Global Fleet`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/globalfleet/globalfleet.module').then(
        m => m.GlobalFleetModule,
      ),
  },
  {
    path: 'local',
    title: $localize`Local Jobs`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/local-jobs/local-jobs.module').then(m => m.LocalJobsModule),
  },
  {
    path: 'vworker',
    title: $localize`VWorker`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/vworker/vworker.module').then(m => m.VWorkerModule),
  },
  {
    path: 'users/kyc',
    title: $localize`Verification Center`,
    loadChildren: () => import('app/kyc/kyc.module').then(m => m.KYCModule),
  },
  {
    path: 'deposit',
    title: $localize`Deposit Funds`,
    data: {
      disableChildNavigationActions: true,
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import(
        'app/payments/payments-deposit/payments-page/payments-page.module'
      ).then(m => m.PaymentsPageModule),
  },
  {
    path: 'cart',
    title: $localize`Process Payment`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import(
        'app/payments/cart/cart-process-page/cart-process-page.module'
      ).then(m => m.CartProcessPageModule),
  },
  {
    path: 'verify',
    title: $localize`Payment Verification`,
    data: {
      disableChildNavigationActions: true,
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import(
        'app/payments/payment-verify/payment-verify-page/payment-verify-page.module'
      ).then(m => m.PaymentVerifyPageModule),
  },
  {
    path: 'payment-share',
    title: $localize`Payment Share`,
    loadChildren: () =>
      import('app/payments/payment-share/payment-share.module').then(
        m => m.PaymentShareModule,
      ),
  },
  {
    path: 'webapp-playground',
    title: $localize`Webapp Playground`,
    data: {
      enableSidebar: true,
    },
    loadChildren: () =>
      import('app/webapp-playground/webapp-playground.module').then(
        m => m.WebappPlaygroundModule,
      ),
  },
  {
    path: 'tutorial',
    title: $localize`Tutorial`,
    loadChildren: () =>
      import('app/tutorial/tutorial.module').then(m => m.TutorialModule),
  },
  {
    path: 'verified',
    title: $localize`Freelancer Verified`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/freelancer-verified/freelancer-verified-landing/freelancer-verified-landing.module'
      ).then(m => m.FreelancerVerifiedLandingModule),
  },
  {
    path: 'reclamation',
    title: $localize`NASA Reclamation`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/nasa/nasa-reclamation-landing/nasa-reclamation-landing.module'
      ).then(m => m.NasaReclamationLandingModule),
  },
  {
    path: 'nist/first-responder-dashboard',
    title: $localize`NIST First Responder Dashboard`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XXLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/nist/nist-landing.module').then(m => m.NistLandingModule),
  },
  {
    path: 'get',
    title: $localize`Freelancer Referrals`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableGiveGetFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/referrals/get/get.routes').then(m => m.GET_ROUTES),
  },
  {
    path: 'give',
    title: $localize`Freelancer Referrals`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableNavigationChild: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/referrals/give/give.module').then(m => m.GiveModule),
  },
  {
    path: 'academy',
    title: $localize`Academy`,
    loadChildren: () =>
      import('app/academy/academy.module').then(m => m.AcademyModule),
  },
  {
    path: 'user/settings',
    title: $localize`User Settings`,
    data: {
      adminOnlyMessaging: true,
      useManualIosKeyboardAvoidance: true,
    },
    loadChildren: () =>
      import('app/user-settings/user-settings.module').then(
        m => m.UserSettingsModule,
      ),
  },
  {
    path: 'users/settings.php',
    title: $localize`User Settings`,
    canActivate: [LegacyUserSettingsGuard],
    component: AppEmptyComponent,
  },
  {
    path: 'me',
    title: $localize`My Profile`,
    loadChildren: () =>
      import('app/user-me/user-me.module').then(m => m.UserMeModule),
  },
  {
    path: 'u/:username',
    title: $localize`User Profile`,
    data: {
      disableStickyNavBar: true,
      enableDarkFixedNavigation: true,
      containerSize: ContainerSize.DESKTOP_XLARGE,
    },
    loadChildren: () =>
      import('app/user-profile/user-profile.module').then(
        m => m.UserProfileModule,
      ),
  },
  {
    path: 'ideas/:username',
    title: $localize`Discover`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/discover-collections/collections.module').then(
        m => m.CollectionsModule,
      ),
  },
  {
    path: 'lists',
    title: $localize`Lists`,
    loadChildren: () =>
      import('app/user-lists/user-lists.module').then(m => m.UserListsModule),
  },
  {
    path: 'tasklists',
    title: $localize`Task Lists`,
    loadChildren: () =>
      import('app/task-lists/task-lists-page.module').then(
        m => m.TaskListsPageModule,
      ),
  },
  {
    path: 'project-updates',
    title: $localize`Project Updates`,
    loadChildren: () =>
      import('app/check-ins/check-ins.module').then(m => m.CheckInsModule),
  },
  {
    path: 'navigation',
    title: $localize`Navigation`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      preloadRequired: true,
    },
    loadChildren: () =>
      import(
        'app/navigation/navigation-primary/navigation-pages/navigation-pages.module'
      ).then(m => m.NavigationPagesModule),
  },
  {
    path: 'logged-out-menu',
    title: $localize`Menu`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import(
        'app/navigation/navigation-logged-out-home/navigation-logged-out-home-page.module'
      ).then(m => m.NavigationLoggedOutHomePageModule),
  },
  {
    path: 'discover',
    title: $localize`Discover`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_LARGE,
      disableStickyNavBar: true,
      disableMessaging: true,
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/discover/discover.module').then(m => m.DiscoverModule),
  },
  {
    path: 'external-references',
    title: $localize`External References`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/external-reference-page/external-reference-page-routing.module'
      ).then(m => m.ExternalReferencePageRoutingModule),
  },
  {
    path: 'payments/transactions',
    title: $localize`Transaction History`,
    loadChildren: () =>
      import('app/transaction-history/transaction-history.module').then(
        m => m.TransactionHistoryModule,
      ),
  },
  {
    path: 'payout',
    title: $localize`Payout`,
    loadChildren: () =>
      import('app/payments/payout/payout.module').then(m => m.PayoutModule),
  },
  {
    path: 'tax',
    title: $localize`Tax Statements`,
    loadChildren: () => import('app/tax/tax.module').then(m => m.TaxModule),
  },
  {
    path: 'showcase',
    title: $localize`Showcase`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_LARGE,
      containerSizeDesktopXLarge: ContainerSize.DESKTOP_XLARGE,
      disableStickyNavBar: true,
      disableMessaging: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/showcase/showcase-page.module').then(
        m => m.ShowcasePageModule,
      ),
  },
  {
    path: 'portfolio-items',
    title: $localize`User Portfolio`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_LARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/portfolio-item-page/portfolio-item-page.module').then(
        m => m.PortfolioItemPageModule,
      ),
  },
  {
    path: 'email-onverify',
    title: $localize`Email Verification`,
    loadChildren: () =>
      import('app/email-onverify/email-onverify.module').then(
        m => m.EmailOnVerifyModule,
      ),
  },
  {
    path: 'email-verify',
    title: $localize`Email Verification`,
    loadChildren: () =>
      import('app/email-verify/email-verify.module').then(
        m => m.EmailVerifyModule,
      ),
  },
  {
    path: 'search',
    title: $localize`Search`,
    canActivate: [RedirectToThemeGuard],
    data: {
      isMobilePrimaryView: true,
      enableSidebar: true,
      enableDarkFixedNavigation: true,
      redirectToThemeFilter: ['freightlancer'],
      preloadModule: 'app/search/search.module',
    },
    loadChildren: () =>
      import('app/search/search.module').then(m => m.SearchModule),
  },
  {
    path: 'googlecloud',
    title: $localize`Google Cloud`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableStickyNavBar: true,
      enableDarkFixedNavigation: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/branded-communities/google-landing/google-landing.module'
      ).then(m => m.GoogleLandingModule),
  },
  {
    path: 'facebook',
    title: $localize`Facebook`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disablePostProjectButton: true,
      disableStickyNavBar: true,
      enableDarkFixedNavigation: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/branded-communities/facebook-landing/facebook-landing.module'
      ).then(m => m.FacebookLandingModule),
  },
  {
    path: 'heygen',
    title: $localize`HeyGen`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disablePostProjectButton: true,
      disableStickyNavBar: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/branded-communities/heygen-landing/heygen-landing.module'
      ).then(m => m.HeyGenLandingModule),
  },
  {
    path: 'groups',
    title: $localize`Groups`,
    data: {
      enableSidebar: true,
      stickyPrimaryNav: true,
      preloadRequired: true,
    },
    loadChildren: () =>
      import('app/groups/groups-routing.module').then(
        m => m.GroupsRoutingModule,
      ),
  },
  {
    path: 'internal',
    title: $localize`Internal`,
    loadChildren: () =>
      import('app/internal/internal.module').then(m => m.InternalModule),
  },
  {
    path: 'manage',
    title: $localize`Manage Your Projects and Contests`,
    data: {
      isMobilePrimaryView: true,
      enableSidebar: true,
      preloadRequired: true,
    },
    loadChildren: () =>
      import('app/manage/manage.module').then(m => m.ManageModule),
  },
  {
    path: 'insights/bids',
    title: $localize`Bid Insights`,
    loadChildren: () =>
      import('app/insights/insights.module').then(m => m.InsightsModule),
    data: {
      containerSize: ContainerSize.DESKTOP_XXLARGE,
    },
  },
  {
    path: 'find',
    title: $localize`Find Freelancers`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/find/find-page.module').then(m => m.FindPageModule),
  },
  {
    path: 'hire/:seoUrl',
    title: $localize`Hire`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/hire/hire-page.module').then(m => m.HirePageModule),
  },
  {
    path: 'hire',
    title: $localize`Hire`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/hire-all-skills/hire-all-skills.module').then(
        m => m.HireAllSkillsModule,
      ),
  },
  {
    path: 'notifications',
    redirectTo: '/navigation/updates',
  },
  {
    path: 'my-projects',
    redirectTo: '/manage',
  },
  {
    path: 'skill-select',
    redirectTo: '/sellers/skill-select.php',
  },
  {
    path: 'verify-phone',
    redirectTo: '/users/phoneverify.php',
  },
  {
    path: 'new-client',
    title: $localize`Welcome to Freelancer`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/client-onboarding/client-onboarding.module').then(
        m => m.ClientOnboardingModule,
      ),
  },
  {
    path: 'new-freelancer',
    title: $localize`Welcome to Freelancer`,
    canActivate: [RedirectToNewCarrierGuard],
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('app/freelancer-onboarding/freelancer-onboarding.module').then(
        m => m.FreelancerOnboardingModule,
      ),
  },
  {
    path: 'membership-trial-offer',
    title: $localize`Start Your Free Plus Membership Trial Now!`,
    data: {
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('app/upsell-trial-membership/upsell-trial-membership.module').then(
        m => m.UpsellTrialMembershipModule,
      ),
  },
  {
    path: 'membership',
    title: $localize`Membership`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/membership/membership.module').then(m => m.MembershipModule),
  },
  {
    path: 'support-feedback',
    title: $localize`Provide Feedback for our Support Team`,
    loadChildren: () =>
      import('app/support-feedback/support-feedback.module').then(
        m => m.SupportFeedbackModule,
      ),
  },
  // To bypass logged out redirect for SSO route
  {
    path: 'saml_sso/sso.php',
    title: $localize`SSO Login`,
    data: {
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    canActivate: [RedirectToPhpGuard],
    component: AppEmptyComponent,
  },
  {
    path: 'users/usersuspended.php',
    title: $localize`Account Suspended`,
    data: {
      bypassLoggedOutRestricted: {
        deloitte: true,
        native: true,
      },
    },
    canActivate: [RedirectToPhpGuard],
    component: AppEmptyComponent,
  },
  {
    path: 'users/l.php',
    title: $localize`Link Redirect`,
    loadChildren: () =>
      import('app/linkshim/linkshim.module').then(m => m.LinkshimModule),
  },
  {
    path: 'support',
    title: $localize`Support`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer', 'deloitte'],
      bypassLoggedOutRestricted: {
        native: true,
      },
    },
    loadChildren: () =>
      import('app/support-page/support-page.module').then(
        m => m.SupportPageModule,
      ),
  },
  {
    path: 'on-behalf-project',
    title: $localize`A project has been posted on your behalf`,
    loadChildren: () =>
      import('app/projects/on-behalf-project/on-behalf-project.module').then(
        m => m.OnBehalfProjectModule,
      ),
  },
  {
    path: 'about',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`About`,
        canActivate: [RedirectToPhpGuard],
        data: {
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        pathMatch: 'full',
        component: AppEmptyComponent,
      },
    ],
  },
  // To bypass logged out redirect for T&Cs
  {
    path: 'about/terms',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`Terms & Conditions`,
        canActivate: [RedirectToPhpGuard],
        data: {
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        pathMatch: 'full',
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'about/chain-of-responsibility-guidelines',
    title: $localize`Chain of Responsibility Guidelines`,
    canActivate: [FreightlancerPageGuard],
    loadChildren: () =>
      import(
        'app/freightlancer/freightlancer-about-chain-of-responsibility-page/freightlancer-about-chain-of-responsibility-page.module'
      ).then(m => m.FreightlancerAboutChainOfResponsibilityPageModule),
  },
  // To bypass logged out redirect for privacy policy
  {
    path: 'about/privacy',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`Privacy Policy`,
        canActivate: [RedirectToPhpGuard],
        data: {
          bypassLoggedOutRestricted: {
            native: true,
          },
        },
        pathMatch: 'full',
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'how-it-works',
    canActivate: [FreightlancerPageGuard],
    children: [
      {
        path: '**',
        title: $localize`How It Works`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'cargo-categories',
    canActivate: [FreightlancerPageGuard],
    children: [
      {
        path: '',
        title: $localize`Cargo Categories`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
      {
        path: ':category',
        title: $localize`Cargo Category`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'verifications',
    canActivate: [FreightlancerPageGuard],
    children: [
      {
        path: '**',
        title: $localize`Verifications`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'transport-routes',
    canActivate: [FreightlancerPageGuard],
    children: [
      {
        path: '',
        title: $localize`Transport Routes`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
      {
        path: ':route',
        title: $localize`Transport Route`,
        canActivate: [RedirectToThemeGuard],
        data: {
          redirectToThemeFilter: ['freightlancer'],
        },
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'user/account-reactivation',
    title: $localize`Account Reactivation`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      bypassLoggedOutRestricted: {
        native: true,
      },
    },
    loadChildren: () =>
      import(
        'app/user-account-reactivation/user-account-reactivation.module'
      ).then(m => m.UserAccountReactivationModule),
  },
  {
    path: 'info/how-it-works',
    title: $localize`How It Works`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
      disableMessaging: true,
    },
    loadChildren: () =>
      import('app/how-it-works-page/how-it-works-page.module').then(
        m => m.HowItWorksPageModule,
      ),
  },
  {
    path: 'info/codeofconduct',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`Code of Conduct`,
        canActivate: [RedirectToPhpGuard],
        pathMatch: 'full',
        component: AppEmptyComponent,
      },
    ],
  },
  {
    path: 'ibm/icct-certification',
    title: $localize`IBM ICCT Certification`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/ibm/icct-certification-landing/icct-certification-landing.module'
      ).then(m => m.IcctCertificationLandingModule),
  },
  {
    path: 'tv',
    title: $localize`Freelancer TV`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/freelancer-tv/freelancer-tv.module').then(
        m => m.FreelancerTVModule,
      ),
  },
  {
    path: 'hire-a-freelancer',
    title: $localize`Hire a Freelancer`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableStickyNavBar: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/hire-a-freelancer/hire-a-freelancer.module').then(
        m => m.HireAFreelancerModule,
      ),
  },
  {
    path: 'corporate/teams/:username',
    title: $localize`Corporate Teams`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/corporate/teams/corporate-teams.module').then(
        m => m.CorporateTeamsModule,
      ),
  },
  {
    path: 'q/:id',
    redirectTo: 'quotation/:id',
  },
  {
    path: 'quotation/:id',
    title: $localize`Quotation Details`,
    loadChildren: () =>
      import('app/quotations/quotations.module').then(m => m.QuotationsModule),
    data: {
      disableFooter: true,
      disableMessaging: false,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
  },
  {
    path: 'bahr-initiative',
    title: $localize`Bahr's Freelancers go International`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/saudi-arabia/bahr-initiative/saudi-arabia-landing.module'
      ).then(m => m.SaudiArabiaLandingModule),
  },
  {
    path: 'tamkeen',
    title: $localize`Freelancer x Tamkeen`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: false,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('app/tamkeen/tamkeen-landing.module').then(
        m => m.TamkeenLandingModule,
      ),
  },
  {
    path: 'innovation-challenge',
    redirectTo: '/innovation',
  },
  {
    path: 'innovation-challenges',
    redirectTo: '/innovation',
  },
  {
    path: 'moonshot',
    redirectTo: '/innovation',
  },
  {
    path: 'innovation',
    title: $localize`Innovation Challenges`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import(
        'app/innovation-challenge/innovation-challenge-landing.module'
      ).then(m => m.InnovationChallengeLandingModule),
  },
  {
    path: 'manufacturing',
    title: $localize`Manufacturing`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/manufacturing/manufacturing-landing.module').then(
        m => m.ManufacturingLandingModule,
      ),
  },
  {
    path: 'generative-ai',
    title: $localize`Generative AI`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/generative-ai/generative-ai-landing.module').then(
        m => m.GenerativeAiLandingModule,
      ),
  },
  // Generate Landing Pages Anchor (DO NOT DELETE! yarn generate:landing-page will look for this line to insert entries)
  {
    path: 'launch-deck',
    title: $localize`Launch Deck`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      disableMessaging: true,
      disableToastNotifications: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/launch-deck/launch-deck-landing.module').then(
        m => m.LaunchDeckLandingModule,
      ),
  },
  {
    path: 'launch',
    title: $localize`Year of the Launch`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XXXLARGE,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/launch/launch-landing.module').then(
        m => m.YearOfTheLaunchLandingModule,
      ),
  },
  {
    path: 'back-to-work',
    title: $localize`Back to Work`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/back-to-work/back-to-work-landing.module').then(
        m => m.BackToWorkLandingModule,
      ),
  },
  {
    path: 'tenders',
    title: $localize`Tenders`,
    canActivate: [FreightlancerPageGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import(
        './freightlancer/loadshift-tender/loadshift-tender-landing.module'
      ).then(m => m.LoadshiftTenderLandingModule),
  },
  {
    path: 'e-commerce-everywhere',
    title: $localize`E-commerce Everywhere`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      disableVerificationBanner: true,
      enableDarkFixedNavigation: true,
      forceDarkMode: true,
    },
    loadChildren: () =>
      import(
        'app/e-commerce-everywhere/e-commerce-everywhere-landing.module'
      ).then(m => m.ECommerceEverywhereLandingModule),
  },
  {
    path: 'invitation-action',
    title: $localize`Invitation Action`,
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      fixedHeight: true,
    },
    loadChildren: () =>
      import('app/invitation-redirect/invitation-redirect.module').then(
        m => m.InvitationRedirectModule,
      ),
  },
  {
    path: 'recruiter',
    title: $localize`Recruiter`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XXLARGE,
      disableMessaging: true,
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/recruiter/recruiter.module').then(m => m.RecruiterModule),
  },
  {
    path: 'dreamfactory',
    title: $localize`Dream Factory`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/dream-factory/dream-factory.module').then(
        m => m.DreamFactoryModule,
      ),
  },
  {
    path: 'start-my-contest',
    title: $localize`Start My Contest`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      disableStickyNavBar: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/sem-contest-page/sem-contest-page.module').then(
        m => m.SemContestPageModule,
      ),
  },
  {
    path: 'get-started',
    title: $localize`Get Started`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/get-started/get-started.module').then(
        m => m.GetStartedModule,
      ),
  },
  {
    path: 'talent-success',
    title: $localize`Talent Success`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/enterprise-talent-success/enterprise-talent-success.module'
      ).then(m => m.EnterpriseTalentSuccessModule),
  },
  {
    path: 'videochat',
    title: $localize`Video Chat`,
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('app/call-window/call-window.module').then(
        m => m.CallWindowModule,
      ),
    data: {
      containerSize: ContainerSize.DESKTOP_XXLARGE,
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
  },
  {
    path: 'verification',
    title: $localize`Verification`,
    data: {
      disableChildNavigationActions: true,
      disableFooter: true,
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
    },
    loadChildren: () =>
      import('app/verification/verification.module').then(
        m => m.VerificationModule,
      ),
  },
  {
    path: 'solutions',
    title: $localize`Solutions`,
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
    },
    loadChildren: () =>
      import('app/solutions/solutions.module').then(m => m.SolutionsModule),
  },
  {
    path: 'download',
    canActivate: [RedirectToThemeGuard],
    title: $localize`Download the Freelancer mobile app!`,
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      bypassLoggedOutRestricted: {
        native: true,
      },
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/download-app/download-app.module').then(
        m => m.DownloadAppModule,
      ),
  },
  {
    path: 'quotes',
    title: $localize`Quotes`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
      preloadModule: 'app/quotes/quotes.module',
    },
    loadChildren: () =>
      import('app/quotes/quotes.module').then(m => m.QuotesModule),
  },
  {
    path: 'preferred-freelancer-program',
    title: $localize`Preferred Freelancer Program`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSizeDesktopXLarge: ContainerSize.DESKTOP_XXLARGE,
      disableMessaging: true,
      enableSeoNav: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () => import('app/pfp/pfp.module').then(m => m.PfpModule),
  },
  {
    path: 'go',
    title: $localize`Hire Freelancers Online.`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/dynamic-landing/dynamic-landing.module').then(
        m => m.DynamicLandingModule,
      ),
  },
  {
    path: 'what-is',
    title: $localize`What Is`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      disableMessaging: true,
      containerSize: ContainerSize.DESKTOP_SMALL,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/master-article/master-article.module').then(
        m => m.MasterArticleModule,
      ),
  },
  {
    path: 'what-is-*',
    title: $localize`What Is`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      disableMessaging: true,
      containerSize: ContainerSize.DESKTOP_SMALL,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/master-article/master-article.module').then(
        m => m.MasterArticleModule,
      ),
  },
  {
    path: 'articles',
    title: $localize`Freelancer Content Hub`,
    canActivate: [RedirectToThemeGuard],
    data: {
      enableSeoNav: true,
      disableMessaging: true,
      containerSize: ContainerSize.DESKTOP_SMALL,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/content-hub/content-hub.module').then(
        m => m.ContentHubModule,
      ),
  },
  {
    path: 'start',
    title: $localize`Start`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/start-landing-page/start-landing-page.module').then(
        m => m.StartLandingPageModule,
      ),
  },
  {
    path: 'service',
    title: $localize`Service Offerings`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: false,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/service-offerings/service-offerings.module').then(
        m => m.ServiceOfferingsModule,
      ),
  },
  {
    path: 'field-services-talent',
    title: $localize`Field Services Talent`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/field-services-talent/field-services-talent.module').then(
        m => m.FieldServicesTalentModule,
      ),
  },
  {
    path: 'get-your-gift',
    title: $localize`Get Your Gift`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/get-your-gift/get-your-gift.module').then(
        m => m.GetYourGiftModule,
      ),
  },
  {
    path: 'crazyideas',
    title: $localize`Crazy Ideas`,
    canActivate: [RedirectToThemeGuard],
    data: {
      containerSize: ContainerSize.DESKTOP_XLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/crazy-ideas/crazy-ideas.module').then(
        m => m.CrazyIdeasModule,
      ),
  },
  {
    path: 'nih/targeted-challenge',
    title: $localize`NIH Targeted Challenge`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/nih-genome-challenge-page/nih-genome-challenge-page.module'
      ).then(m => m.NihGenomeChallengePageModule),
  },
  {
    path: 'harvard-d3',
    title: $localize`Harvard D3`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/harvard/harvard-d3/harvard-d3.module').then(
        m => m.HarvardD3Module,
      ),
  },
  {
    path: 'users/onverify.php',
    title: $localize`Email Verification`,
    canActivate: [RedirectToPhpGuard],
    component: AppEmptyComponent,
  },
  {
    path: 'post-availability',
    title: $localize`Post Availability`,
    data: {
      adminOnlyMessaging: true,
      disableNavigation: true,
      disableNavigationChild: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      isMobilePrimaryView: true,
      disableFooter: true,
    },
    loadChildren: () =>
      import(
        'app/user-availability/user-availability-create-page/user-availability-create-page.module'
      ).then(m => m.UserAvailabilityCreatePageModule),
  },
  {
    path: 'availabilities',
    title: $localize`Availabilities`,
    loadChildren: () =>
      import(
        'app/user-availability/user-availability-detail-page/user-availability-detail-page.module'
      ).then(m => m.UserAvailabilityDetailPageModule),
  },
  {
    path: 'pod25',
    title: $localize`Hire Freelancers`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/bonus/bonus.module').then(m => m.BonusModule),
  },
  {
    path: 'spotify',
    title: $localize`Spotify Bonus`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/bonus/bonus.module').then(m => m.BonusModule),
  },
  {
    path: 'offer25',
    title: $localize`Offer25 Bonus`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/bonus/bonus.module').then(m => m.BonusModule),
  },
  {
    path: 'smb',
    title: $localize`Freelancer for Small & Medium Business`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableMessaging: true,
      disableNavigation: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      disableFooter: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/small-medium-business/small-medium-business-page.module'
      ).then(m => m.SmallMediumBusinessPageModule),
  },
  {
    path: 'ai',
    title: $localize`Supercharge your Business with AI`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableGiveGetFooter: true,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      containerSize: ContainerSize.DESKTOP_XLARGE,
      forceDarkMode: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/back-to-work/back-to-work.routes').then(
        m => m.BACK_TO_WORK_ROUTES,
      ),
  },
  {
    path: 'ai-for-business',
    title: $localize`AI for Business`,
    canActivate: [RedirectToThemeGuard],
    loadChildren: () =>
      import('app/ai-for-business/ai-for-business.routes').then(
        m => m.AI_FOR_BUSINESS_ROUTES,
      ),
  },
  {
    path: 'nasa',
    title: $localize`NASA Tournament Lab`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableStickyNavBar: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import(
        'app/nasa/nasa-tournament-lab-landing/nasa-tournament-lab-landing.module'
      ).then(m => m.NasaTournamentLabLandingModule),
  },
  {
    path: 'red-leaf-securities',
    title: $localize`Red Leaf Securities`,
    data: {
      disableStickyNavBar: true,
    },
    loadChildren: () =>
      import('app/red-leaf-securities/red-leaf-securities.module').then(
        m => m.RedLeafSecuritiesModule,
      ),
  },
  {
    path: 'freemarket',
    title: $localize`Freemarket`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
      forceDarkMode: true,
    },
    loadChildren: () =>
      import('app/service-landing-page/service-landing-page.module').then(
        m => m.ServiceLandingPageModule,
      ),
  },
  {
    path: 'ava',
    title: $localize`Supercharge your business with AI`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableGiveGetFooter: true,
      disableMessaging: true,
      disableToastNotifications: true,
      disableVerificationBanner: true,
      containerSize: ContainerSize.DESKTOP_XLARGE,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/ava/ava-landing-page.routes').then(
        m => m.AVA_LANDING_PAGE_ROUTES,
      ),
  },
  {
    path: 'shops',
    title: $localize`Freelancer Shopfront`,
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
      enableDarkFixedNavigation: true,
    },
    loadChildren: () =>
      import(
        'app/user-profile/user-profile-shopfront/user-profile-shopfront.module'
      ).then(m => m.UserProfileShopfrontModule),
  },
  {
    path: 'board',
    title: $localize`Prototyper Board`,
    canActivate: [RedirectToThemeGuard],
    data: {
      disableFooter: true,
      disableVerificationBanner: true,
      redirectToThemeFilter: ['freightlancer'],
    },
    loadChildren: () =>
      import('app/prototyper/prototyper.module').then(m => m.PrototyperModule),
  },
  {
    path: 'prototyper',
    canActivate: [RedirectToThemeGuard],
    data: {
      redirectToThemeFilter: ['freightlancer'],
    },
    children: [
      {
        path: '',
        title: $localize`Prototyper`,
        canActivate: [RedirectToThemeGuard],
        data: {
          containerSize: ContainerSize.DESKTOP_XLARGE,
          disableMessaging: true,
          disableVerificationBanner: true,
          enableDarkFixedNavigation: true,
        },
        loadChildren: () =>
          import(
            'app/prototyper/prototyper-landing-page/prototyper-landing.module'
          ).then(m => m.PrototyperLandingModule),
      },
      {
        path: 'dashboard',
        title: $localize`Prototyper Dashboard`,
        loadChildren: () =>
          import('app/prototyper-dashboard/prototyper-dashboard.module').then(
            m => m.PrototyperDashboardModule,
          ),
      },
    ],
  },
  {
    path: 'moon-mascot',
    pathMatch: 'full',
    redirectTo: '/contest/2527909',
  },
  {
    path: 'boom',
    pathMatch: 'full',
    redirectTo: '/contest/2693051',
  },
  /**
   * This will internally redirect (ie. the user wont see the the url changing)
   * urls with the structure `what-is-<seo_url>` to `what-is/<seo_url>`.
   *
   * This is done to resolve the issue with the guess parser (see Q54).
   */
  {
    matcher: masterArticleRouteMatcher,
    canActivate: [RedirectToRootMasterArticleGuard],
    component: AppEmptyComponent,
  },
  /**
   * This maps routes starting with /jobs and handles them with webapp instead
   * of PHP.
   */
  {
    matcher: jobsRouteMatcher,
    redirectTo: 'projects',
  },
  // Catch-all route
  // TODO: T267853 - instrument that so we can know which non-webapp pages are the most
  // visited, i.e. to prioritize future migrations.
  {
    path: '**',
    title: $localize`Page Not Found`,
    canActivate: [RedirectToPhpGuard],
    component: AppEmptyComponent,
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes), AppEmptyModule],
  providers: [],
  exports: [RouterModule],
})
export class AppRoutingModule {}
